
import { SearchComponent } from "../../../../components/components/js/SearchComponent";
import { AdmissionTable } from "./components/AdmissionTable";
import { HeaderComponent } from "../../../../components/components/js/HeaderComponent";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import AuthContext from "../../../../context/AuthProvider";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import { useState, useEffect, useCallback, useContext } from "react";
import Select from "react-select";
import { useTheme } from "@emotion/react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { LoadingDots } from "../../../../components/general/LoadingDots";
import Pagination from "../../../../components/general/Pagination";

export const AdmissionFormOverview = () => {
  const { t, i18n } = useTranslation(); 
  const isAr = i18n.language === 'ar';
  const muiTheme = useTheme();
  const { school } = useContext(AuthContext);
  const axiosPrivate = useAxiosPrivate();

  const [admissions, setAdmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [searchQuery, setSearchQuery] = useState("");
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [previousPage, setPreviousPage] = useState(null);
  const PAGE_SIZE = 10;

  // Fetch admissions data
  const fetchAdmissions = useCallback(async (page = 1) => {
    if (!school?.id) return;
    
    setLoading(true);
    try {
      // Build query parameters
      let url = `${process.env.REACT_APP_BASE_URL}/en/api/nursery/admission-applications/children-overview/?nursery_id=${school.id}&page=${page}&page_size=${PAGE_SIZE}`;
      
      // Add status filter if selected
      if (statusFilter) {
        url += `&status=${statusFilter}`;
      }
      
      // Add search query if exists
      if (searchQuery) {
        url += `&search=${encodeURIComponent(searchQuery)}`;
      }
      
      const response = await axiosPrivate.get(url);
      
      setAdmissions(response.data.results);
       setTotalCount(response.data.total_items || response.data.count);
      setTotalPages(response.data.total_pages || 1); 
      setNextPage(response.data.links?.next || response.data.next);
      setPreviousPage(response.data.links?.previous || response.data.previous);
      setCurrentPage(page);
    } catch (error) {
      toast.error(isAr ? "فشل في تحميل بيانات التقديم" : "Failed to load admissions");
    } finally {
      setLoading(false);
    }
  }, [axiosPrivate, school?.id, statusFilter, searchQuery, t]);

  // Initial fetch
  useEffect(() => {
    fetchAdmissions(1);
  }, [statusFilter]); // Refetch when status filter changes

  // Search functionality - debounced
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (searchQuery !== undefined) {
        fetchAdmissions(1);
      }
    }, 500); // 500ms debounce

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  // Handle search input
  const handleSearch = (searchValue) => {
    setSearchQuery(searchValue);
  };

  // Status filter functionality
  const handleStatusFilter = (selected) => {
    const status = selected?.value || "";
    setStatusFilter(status);
  };

  // Pagination handlers
  const handleNextPage = () => {
    if (nextPage) {
      fetchAdmissions(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (previousPage) {
      fetchAdmissions(currentPage - 1);
    }
  };

  // Action handlers
  const handleApprove = async (rowData) => {
    try {
      await axiosPrivate.post(
        `${process.env.REACT_APP_BASE_URL}/en/api/nursery/admission-child/${rowData.id}/approve/`
      );
      toast.success(isAr ? "تمت الموافقة على الطلب بنجاح" : "Application approved successfully");
      fetchAdmissions(currentPage); // Refresh current page
    } catch (error) {
      console.error("Error approving application:", error);
      toast.error(isAr ? "فشل في الموافقة على الطلب" : "Failed to approve application");
    }
  };

  const handleReject = async (rowData) => {
    try {
      await axiosPrivate.post(
        `${process.env.REACT_APP_BASE_URL}/en/api/nursery/admission-child/${rowData.id}/reject/`
      );
      toast.success(isAr ? "تم رفض الطلب بنجاح" : "Application rejected successfully");
      fetchAdmissions(currentPage); // Refresh current page
    } catch (error) {
      console.error("Error rejecting application:", error);
      toast.error(isAr ? "فشل في رفض الطلب" : "Failed to reject application");
    }
  };

  const handleEdit = (rowData) => {

  };

  // Search filter options - only child name
  const searchFilterOptions = [
    { value: "child_name", label: t("search.child_name") || "Child Name" },
  ];

  // Status filter options
  const statusOptions = [
    { value: "", label: t("status.all") || "All Status" },
    { value: "pending", label: t("status.pending") || "Pending" },
    { value: "approved", label: t("status.approved") || "Approved" },
    { value: "rejected", label: t("status.rejected") || "Rejected" },
  ];

  const customSelectStyles = {
    control: (base) => ({
      ...base,
      border: "none",
      boxShadow: "none",
      cursor: "pointer",
      minHeight: "40px",
      backgroundColor: "#FCFAFA",
      borderRadius: "4px",
    }),
    menu: (base) => ({
      ...base,
      marginTop: "4px",
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? muiTheme.palette.primary.main
        : state.isFocused
        ? muiTheme.palette.primary.main + "20"
        : "#fff",
      color: state.isSelected ? "#fff" : "#000",
      padding: "8px 12px",
    }),
  };

  return (
    <div className="container-fluid px-4">
      {/* Header - title only, no buttons */}
      <HeaderComponent title="admission_overview" />

      {/* Search and Status Filter */}
      <div className="d-flex align-items-center gap-2 mb-3">
        <div style={{ flex: 1 }}>
          <SearchComponent
            items={searchFilterOptions}
            onSearch={handleSearch}
            placeholder="search_by_child_name"
          />
        </div>
        <div style={{ width: "180px" , height: "60px" }}>
          <Select
            options={statusOptions}
            placeholder={t("status.filter") || "Status"}
            isSearchable={false}
            isClearable
            value={statusOptions.find(option => option.value === statusFilter)}
            styles={customSelectStyles}
            onChange={handleStatusFilter}
          />
        </div>
      </div>

      {/* Loading State */}
      {loading && <LoadingDots />}

      {/* Empty State or Error - Show only message */}
      {!loading && admissions.length === 0 && (
        <div className="text-center py-5" style={{ minHeight: "300px", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <h3 className="fw-bold text-muted">
            {t("nodata") || "No admission applications found"}
          </h3>
        </div>
      )}

      {/* Table - Only show if there's data */}
      {!loading && admissions.length > 0 && (
        <>
          <AdmissionTable
            data={admissions}
            onApprove={handleApprove}
            onReject={handleReject}
            onEdit={handleEdit}
          />

      {/* Pagination */}
          <Pagination 
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            nextPage={nextPage}
            previousPage={previousPage}
            onPageChange={(page) => fetchAdmissions(page)}
            pageSize={PAGE_SIZE} 
          />
       
        </>
      )}
    </div>
  );
};